import * as THREE from 'three'
import Experience from './Experience.js'
import params, { BLOOM_LAYER } from './params.js'

import { UnrealBloomPass } from 'three/examples/jsm/postprocessing/UnrealBloomPass.js';
import { RenderPass } from 'three/examples/jsm/postprocessing/RenderPass.js';
import { EffectComposer } from 'three/examples/jsm/postprocessing/EffectComposer.js';

export default class Renderer {
    constructor() {
        this.experience = new Experience()
        this.canvas = this.experience.canvas
        this.sizes = this.experience.sizes
        this.scene = this.experience.scene
        this.camera = this.experience.camera
        this.debug = this.experience.debug
        this.setInstance()
        this.setBloom()
    }

    setInstance() {
        this.instance = new THREE.WebGLRenderer({
            canvas: this.canvas,
            antialias: true
        })
        // this.instance.physicallyCorrectLights = true
        // this.instance.outputEncoding = THREE.sRGBEncoding
        this.setReinhardTone()
        // this.instance.shadowMap.enabled = true
        // this.instance.shadowMap.type = THREE.PCFSoftShadowMap
        this.instance.setClearColor('#011020')
        this.instance.setSize(this.sizes.width, this.sizes.height)
        this.instance.setPixelRatio(this.pixelRatio())
    }

    pixelRatio() {
        return Math.min(this.sizes.pixelRatio, 2)
    }

    /**
     * The glow is a blur, so it does not need full retina resolution. Capping
     * the post chain at 1.5 cuts its fill cost by roughly 45% on 2x displays
     * with no visible difference, which is the headroom weaker devices need.
     */
    bloomPixelRatio() {
        return Math.min(this.sizes.pixelRatio, 1.5)
    }

    /**
     * Selective bloom, text meshes only.
     *
     * The scene itself is still drawn straight to the canvas exactly as before,
     * so no colour or tone mapping changes. The glow is built separately: the
     * bloom layer is rendered over black into its own composer, blurred, then
     * blended additively on top. Only pixels the text lights up are touched.
     */
    setBloom() {
        this.bloomComposer = new EffectComposer(this.instance)
        this.bloomComposer.renderToScreen = false
        this.bloomComposer.setPixelRatio(this.bloomPixelRatio())
        this.bloomComposer.setSize(this.sizes.width, this.sizes.height)

        const bloomRender = new RenderPass(this.scene, this.camera.instance)
        bloomRender.clearColor = new THREE.Color(0x000000)
        bloomRender.clearAlpha = 1
        this.bloomComposer.addPass(bloomRender)

        this.bloomPass = new UnrealBloomPass(
            new THREE.Vector2(this.sizes.width, this.sizes.height),
            params.bloomStrength,
            params.bloomRadius,
            params.bloomThreshold
        )
        this.bloomComposer.addPass(this.bloomPass)

        this.glowScene = new THREE.Scene()
        this.glowCamera = new THREE.OrthographicCamera(-1, 1, 1, -1, 0, 1)
        this.glowScene.add(new THREE.Mesh(
            new THREE.PlaneGeometry(2, 2),
            new THREE.MeshBasicMaterial({
                map: this.bloomComposer.renderTarget2.texture,
                blending: THREE.AdditiveBlending,
                transparent: true,
                depthTest: false,
                depthWrite: false,
                toneMapped: false
            })
        ))

        if (this.debug.active && this.debug.ui) {
            this.debugFolder = this.debug.ui.addFolder('bloom')
            this.debugFolder.add(this.bloomPass, 'strength').min(0).max(3).step(0.01)
            this.debugFolder.add(this.bloomPass, 'radius').min(0).max(3).step(0.01)
            this.debugFolder.add(this.bloomPass, 'threshold').min(0).max(1).step(0.01)
        }
    }

    setReinhardTone() {
        this.instance.toneMapping = THREE.ReinhardToneMapping
        this.instance.toneMappingExposure = 15
    }

    setNoTone() {
        this.instance.toneMapping = THREE.NoToneMapping
        this.instance.toneMappingExposure = 1.6
    }

    resize() {
        this.instance.setSize(this.sizes.width, this.sizes.height)
        this.instance.setPixelRatio(this.pixelRatio())
        this.bloomComposer.setSize(this.sizes.width, this.sizes.height)
        this.bloomComposer.setPixelRatio(this.bloomPixelRatio())
    }

    update() {
        const camera = this.camera.instance

        // 1. glow source: bloom-layer objects over black
        const layers = camera.layers.mask
        camera.layers.set(BLOOM_LAYER)
        this.bloomComposer.render()
        camera.layers.mask = layers

        // 2. the scene, untouched
        this.instance.setRenderTarget(null)
        this.instance.render(this.scene, camera)

        // 3. glow on top
        this.instance.autoClear = false
        this.instance.render(this.glowScene, this.glowCamera)
        this.instance.autoClear = true
    }
}
